/**
 * Pair an AI-proposed tier with an existing tier that is ANOTHER SPELLING of
 * the same listed grade — proposal "Elaboré", existing "Special (Elaboré)",
 * with the manufacturer listing "Special (Elaboré)" as a spelling of Elaboré.
 *
 * The enrichCalibre prompt tells the model to return the listed name and says
 * "the curator's tools merge it into the existing tier". This is that merge's
 * matcher: the apply dialog pre-maps the proposal onto the existing tier and
 * offers to rename it to the listed name, instead of appending a duplicate.
 */

import type { CalibreTier } from '../../services/firebase';
import { resolveTypedTierName, type TierNameOption } from '../../utils/tierVocabulary';

export interface VocabRenameTarget {
  tier: CalibreTier;
  /** The listed (canonical) name both spellings resolve to. */
  to: string;
}

export function vocabRenameTarget(
  proposedName: string,
  tiers: CalibreTier[],
  options: TierNameOption[],
): VocabRenameTarget | null {
  const proposed = resolveTypedTierName(proposedName, options);
  const canonical = proposed.kind === 'exact' ? proposed.name : proposed.kind === 'spelling' ? proposed.canonical : null;
  if (!canonical) return null;
  const lower = proposedName.trim().toLowerCase();
  // Exact-name matches are the dialog's ordinary merge path, not this one.
  if (tiers.some((t) => t.name.trim().toLowerCase() === lower)) return null;
  const hits = tiers.filter((t) => {
    if (t.tierType === 'brand-specific') return false;
    const r = resolveTypedTierName(t.name, options);
    return (r.kind === 'exact' ? r.name : r.kind === 'spelling' ? r.canonical : null) === canonical;
  });
  // Two stored tiers spelling one grade is a curator problem — never guess.
  if (hits.length !== 1) return null;
  return { tier: hits[0], to: canonical };
}
